import React, { useState } from 'react';
import { Handle, Position } from 'reactflow';
import SettingsIcon from '@mui/icons-material/Settings';
import CloseIcon from '@mui/icons-material/Close';
// import TriggerModal from './TriggerModal.tsx';
const TriggerModal = React.lazy(() => import('./TriggerModal.tsx'));
import OpenAINode from './Node/OpenAINode.tsx';
import TextNode from './Node/TextNode.tsx';
import ImageNode from './Node/ImageNode.tsx';
import VideoNode from './Node/VideoNode.tsx';
import ButtonNode from './Node/ButtonNode.tsx';
import ListNode from './Node/ListNode.tsx';
import DocumentNode from './Node/DocumentNode.tsx';
import AudioNode from './Node/AudioNode.tsx';
import RequestInterventionNode from './Node/RequestInterventionNode.tsx';
import CatalogNode from './Node/CatalogNode.tsx';
import ProductNode from './Node/ProductNode.tsx';
import WhatsAppFlowNode from './Node/WhatsappFlowNode.tsx';
import ExistingFlowNode from './Node/ExistingFlowNode.tsx';
import { botFlowDataAtom, flowValidationAtom } from '../../../Services/_states/index.js';
import { useRecoilValue } from 'recoil';
import AddressNode from './Node/AddressNode.tsx';
import TemplateNode from './Node/TemplateNode.tsx';
import CtaUrlNode from './Node/CtaUrlNode.tsx';
// import KnowledgeBaseNode from './Node/KnowledgeBaseNode.tsx';
import { Box, Button, Tooltip } from '@mui/material';
import ModalForms from '../../Shared/Modals/index.js';
import MapIcon from '@mui/icons-material/Map';
import ConditionalNode from './Node/ConditionalNode.tsx';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

// Color map for node types
const nodeColors: { [key: string]: string } = {
  text: '#1E88E5', // Distinct Blue
  image: '#43A047', // Distinct Green
  video: '#E53935', // Distinct Red
  button: '#D81B60', // Distinct Pink
  list: '#8E24AA', // Distinct Purple
  document: '#F4511E', // Distinct Orange
  audio: '#00ACC1', // Distinct Cyan
  intervention: '#6D4C41', // Distinct Brown
  catalog: '#3949AB', // Distinct Deep Blue
  whatsAppFlow: '#25D366',
  existingFlow: '#00ACC1',
  address: '#1E88E5',
  template: '#7B1FA2',
  openAi: '#000',
  ctaUrl: '#FF9800',
  // knowledgeBase: '#4CAF50',
  conditional: '#9b4cafff',
};

const BaseNode = ({ data }: any) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const flowValidationObj = useRecoilValue(flowValidationAtom);
  const botFlowData = useRecoilValue(botFlowDataAtom);
  const { onNodesDelete,onCloneNode } = data;
  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
  };

  const handleDelete = () => {
    if (data?.id && typeof onNodesDelete === 'function') {
      // onNodesDelete([{ id: data.id }]);
      onNodesDelete(data.id);
    } else {
      console.error('onNodesDelete is not a function or data.id is missing.');
    }
  };

  const getNodeComponent = () => {
    switch (data.type) {
      case 'text':
        return <TextNode data={data} />;
      case 'image':
        return <ImageNode data={data} />;
      case 'video':
        return <VideoNode data={data} />;
      case 'button':
        return <ButtonNode data={data} />;
      case 'list':
        return <ListNode data={data} />;
      case 'document':
        return <DocumentNode data={data} />;
      case 'audio':
        return <AudioNode data={data} />;
      case 'intervention':
        return <RequestInterventionNode data={data} />;
      case 'catalog':
        return <CatalogNode data={data} />;
      case 'product':
        return <ProductNode data={data} />;
      case 'whatsAppFlow':
        return <WhatsAppFlowNode data={data} />;
      case 'existingFlow':
        return <ExistingFlowNode data={data} botFlowData={botFlowData ?? null} />;
      case 'address':
        return <AddressNode data={data} />;
      case 'template':
        return <TemplateNode data={data} />;
      case 'openAi':
        return <OpenAINode data={data} />;
      case 'ctaUrl':
        return <CtaUrlNode data={data} />;
      // case 'knowledgeBase':
      //   return <KnowledgeBaseNode data={data} />;
      case 'conditional':
        return <ConditionalNode data={data} />;

      default:
        return null;
    }
  };

  return (
    <div
      style={{
        // border: '1px solid #ddd',
        border: flowValidationObj[data.id] ? '2px solid red' : '1px solid #ddd',
        borderRadius: 8,
        background: '#fff',
        boxShadow: '0 2px 5px rgba(0, 0, 0, 0.1)',
        width: 300,
        position: 'relative',
      }}
    >
      {/* Target Handle */}
      <Handle
        type="target"
        position={Position.Left}
        style={{
          background: '#555',
          height: '10px',
          width: '10px',
          borderRadius: '50%',
          top: '50%',
          transform: 'translateY(-50%)',
        }}
      />

      {/* Source Handle */}
      {data.type !== 'button' &&
        data.type !== 'list' &&
        data.type !== 'ctaUrl' &&
        data.type !== 'intervention' &&
        data.type !== 'whatsappFlow' &&
        data.type !== 'existingFlow' &&
        data.type !== 'conditional' &&
        data.type !== 'catalog' && (
          <Tooltip title="Click to add or Drag to Connect Node" arrow>
            <Handle
              type="source"
              position={Position.Right}
              style={{
                background: '#555',
                height: '10px',
                width: '10px',
                borderRadius: '50%',
                top: '50%',
                transform: 'translateY(-50%)',
              }}
              onClick={() => data.onOutputClick?.(data.id)}
            />
          </Tooltip>
        )}

      {/* Header with Badge Color */}
      <div
        style={{
          background: nodeColors[data.type] || '#ccc',
          color: '#fff',
          borderTopLeftRadius: 8,
          borderTopRightRadius: 8,
          padding: 10,
          textAlign: 'center',
          fontWeight: 'bold',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        {data?.nodeCount}.{' '}
        {data?.triggerSettings?.serialNumber || data?.triggerSettings?.subBotName ? (
          <>
            {data?.triggerSettings?.serialNumber}
            {data?.triggerSettings?.serialNumber && data?.triggerSettings?.subBotName ? ', ' : ''}
            {data?.triggerSettings?.subBotName}
          </>
        ) : null}{' '}
        {data?.type?.charAt(0)?.toUpperCase() + data?.type?.slice(1)} Node
        {/* {data?.nodeCount} {data?.type?.charAt(0)?.toUpperCase() + data?.type?.slice(1)} Node */}
        <div style={{ display: 'flex', gap: 5 }}>
          {/* {data.type === 'list' && botFlowData?.entityForm?._id && (
            <MapIcon style={{ cursor: 'pointer', fontSize: 18 }}
              onClick={() => setIsEntityModalOpen(true)}
            />
          )} */}
          {data.type !== 'conditional' && (
            <SettingsIcon style={{ cursor: 'pointer', fontSize: 18 }} onClick={toggleModal} />
          )}
          {/* <ContentCopyIcon
            style={{ cursor: 'pointer', fontSize: 18 }}
            onClick={() => onCloneNode(data?.id)}
          /> */}

          <CloseIcon style={{ cursor: 'pointer', fontSize: 18 }} onClick={handleDelete} />
        </div>
      </div>
      {/* Node Content */}
      <div style={{ padding: 10 }}>{getNodeComponent()}</div>

      {/* Trigger Modal */}
      {isModalOpen && (
        <TriggerModal
          data={data}
          isOpen={isModalOpen}
          onClose={toggleModal}
          // onSave={(settings: any) => data.onChange(data.id, { triggerSettings: settings })}
        />
      )}
    </div>
  );
};

export default BaseNode;
